// Anomaly Detection Agent UI (Polished)
// Additions: summary strip, velocity trend line chart, risk distribution donut,
// severity bars, explainability panel per anomaly.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Typography, Chip, Stack, Paper, Grid, Divider,
    FormControlLabel, Switch,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, AIAnalysisBox, SectionHeader, DrillTable, EmptyState,
    LoadingBlock, SimpleBarChart, DonutChart, TrendChart, RadialGauge,
    SeverityBadge, ConfidenceBar, fmtMoney, fmtDate, CHART_COLORS,
} from './AgentUIKit';
import {
    anomalyBaseline, anomalyAmount, anomalyVelocity, anomalyNewCounterparty,
    anomalyDuplicates, anomalyWeekend, anomalyCompositeScan,
} from './api';

function riskToNumber(r) { return r === 'HIGH' ? 90 : r === 'MEDIUM' ? 60 : r === 'LOW' ? 30 : 10; }
function riskColor(r) { return r === 'HIGH' ? CHART_COLORS.danger : r === 'MEDIUM' ? CHART_COLORS.warning : CHART_COLORS.success; }

const TABS = ['Composite Scan', 'Baseline', 'Amount', 'Velocity', 'New Counterparties', 'Duplicates', 'Weekend / Off-hours'];

export default function AnomalyDetectionAgent() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [useAi, setUseAi] = useState(true);

    const [composite, setComposite] = useState(null);
    const [baseMerchant, setBaseMerchant] = useState('');
    const [baseline, setBaseline] = useState(null);
    const [amtTxnId, setAmtTxnId] = useState('');
    const [amtResult, setAmtResult] = useState(null);
    const [velocity, setVelocity] = useState(null);
    const [velWindow, setVelWindow] = useState(24);
    const [newCp, setNewCp] = useState(null);
    const [dupes, setDupes] = useState(null);
    const [weekend, setWeekend] = useState(null);

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    const loadComposite = () => run(() => anomalyCompositeScan(useAi)).then(r => r?.data?.success && setComposite(r.data.data));

    useEffect(() => {
        if (tab === 0 && !composite) loadComposite();
        if (tab === 3 && !velocity) run(() => anomalyVelocity(velWindow)).then(r => r?.data?.success && setVelocity(r.data.data));
        if (tab === 4 && !newCp) run(() => anomalyNewCounterparty(7)).then(r => r?.data?.success && setNewCp(r.data.data));
        if (tab === 5 && !dupes) run(() => anomalyDuplicates(7)).then(r => r?.data?.success && setDupes(r.data.data));
        if (tab === 6 && !weekend) run(() => anomalyWeekend(30)).then(r => r?.data?.success && setWeekend(r.data.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const summaryCards = composite ? [
        { label: 'Overall Risk', value: composite.overall_risk || '—', accent: composite.overall_risk === 'HIGH' ? 'danger' : composite.overall_risk === 'MEDIUM' ? 'warning' : 'success' },
        { label: 'Risk Signals', value: composite.risk_signal_count ?? 0, accent: 'info' },
        { label: 'Duplicate Candidates', value: composite.duplicate_payments?.count ?? 0, accent: composite.duplicate_payments?.count > 0 ? 'danger' : 'neutral' },
        { label: 'New Counterparties', value: composite.new_counterparties?.count ?? 0, accent: 'primary' },
    ] : [
        { label: 'Overall Risk', value: '—', accent: 'neutral' },
        { label: 'Risk Signals', value: '—', accent: 'neutral' },
        { label: 'Duplicate Candidates', value: '—', accent: 'neutral' },
        { label: 'New Counterparties', value: '—', accent: 'neutral' },
    ];

    // Build risk distribution donut
    const riskDonutData = composite ? [
        { name: 'Velocity Spikes', value: composite.velocity?.spike_detected ? 1 : 0 },
        { name: 'New Counterparties', value: composite.new_counterparties?.count || 0 },
        { name: 'Duplicates', value: composite.duplicate_payments?.count || 0 },
        { name: 'Benford Suspicious', value: composite.benford_analysis?.suspicious ? 1 : 0 },
    ].filter(d => d.value > 0) : [];

    const renderTab = () => {
        switch (tab) {
            case 0:
                return composite ? (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Composite Risk Scan"
                            subtitle="Unified analysis across velocity, counterparties, duplicates, Benford's Law"
                            icon={<SecurityIcon color="error" />}
                            actions={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <FormControlLabel
                                        control={<Switch size="small" checked={useAi} onChange={e => setUseAi(e.target.checked)} />}
                                        label={<Typography variant="caption">AI Summary</Typography>}
                                    />
                                    <Button size="small" variant="outlined" startIcon={<RefreshIcon />}
                                        onClick={() => { setComposite(null); loadComposite(); }}>Rescan</Button>
                                </Stack>
                            }
                        />
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="caption" fontWeight={600}>Overall Risk Score</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <RadialGauge
                                            value={riskToNumber(composite.overall_risk)}
                                            label={composite.overall_risk}
                                            color={riskColor(composite.overall_risk)}
                                        />
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="caption" fontWeight={600}>Risk Signals Breakdown</Typography>
                                    {riskDonutData.length > 0 ? (
                                        <DonutChart data={riskDonutData} height={240} />
                                    ) : <EmptyState message="No risk signals detected." />}
                                </Paper>
                            </Grid>
                        </Grid>
                        <AIAnalysisBox text={composite.ai_summary} title="AI Risk Summary" />
                        <Grid container spacing={1}>
                            <Grid item xs={6} md={3}>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="caption" fontWeight={600}>Velocity</Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        <Chip size="small"
                                            color={composite.velocity?.spike_detected ? 'error' : 'success'}
                                            label={composite.velocity?.spike_detected ? composite.velocity.severity : 'Normal'} />
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="caption" fontWeight={600}>New Counterparties</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{composite.new_counterparties?.count || 0}</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="caption" fontWeight={600}>Duplicate Payments</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: composite.duplicate_payments?.count > 0 ? 'error.main' : 'text.primary' }}>
                                        {composite.duplicate_payments?.count || 0}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="caption" fontWeight={600}>Benford Analysis</Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        <Chip size="small"
                                            color={composite.benford_analysis?.suspicious ? 'error' : 'success'}
                                            label={composite.benford_analysis?.suspicious ? 'Suspicious' : 'Normal'} />
                                    </Box>
                                    <Typography variant="caption" display="block" color="text.secondary">
                                        deviation: {composite.benford_analysis?.deviation?.toFixed(3)}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                        {composite.new_counterparties?.high_risk_items?.length > 0 && (
                            <>
                                <Typography variant="caption" fontWeight={600}>High-Risk New Counterparties</Typography>
                                <DrillTable
                                    columns={[
                                        { key: 'merchant', label: 'Merchant' },
                                        { key: 'amount', label: 'Amount', align: 'right', render: r => fmtMoney(r.amount) },
                                        { key: 'date', label: 'Date', render: r => fmtDate(r.date) },
                                        { key: 'risk_score', label: 'Risk', align: 'right', render: r => <Chip size="small" color="error" label={r.risk_score} /> },
                                    ]}
                                    rows={composite.new_counterparties.high_risk_items}
                                    rowKeyField="transaction_id"
                                />
                            </>
                        )}
                    </Stack>
                ) : <LoadingBlock rows={4} />;

            case 1:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Behavioral Baseline Builder" subtitle="Rolling 90-day statistics per merchant/category" />
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Merchant (optional)" value={baseMerchant}
                                onChange={e => setBaseMerchant(e.target.value)} />
                            <Button size="small" variant="contained" disabled={loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => anomalyBaseline({
                                        merchant_name: baseMerchant || undefined, days: 90,
                                    }));
                                    if (r?.data?.success) setBaseline(r.data.data);
                                }}>Build Baseline</Button>
                        </Stack>
                        {baseline?.baseline ? (
                            <>
                                <SummaryGrid cards={[
                                    { label: 'Avg Amount', value: fmtMoney(baseline.baseline.avg_amount), accent: 'primary' },
                                    { label: 'Std Deviation', value: fmtMoney(baseline.baseline.std_dev), accent: 'info' },
                                    { label: 'Median', value: fmtMoney(baseline.baseline.median_amount), accent: 'secondary' },
                                    { label: 'Observations', value: baseline.count, accent: 'neutral' },
                                ]} />
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="caption" fontWeight={600}>Counterparty Diversity</Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 700 }}>{baseline.baseline.distinct_counterparties}</Typography>
                                            <Typography variant="caption" color="text.secondary">unique merchants</Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="caption" fontWeight={600}>Benford's Law</Typography>
                                            <Box sx={{ mt: 0.5 }}>
                                                <Chip size="small"
                                                    color={baseline.baseline.benford_suspicious ? 'error' : 'success'}
                                                    label={baseline.baseline.benford_suspicious ? 'Suspicious' : 'Normal'} />
                                            </Box>
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                deviation: {baseline.baseline.benford_deviation?.toFixed(4)}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </>
                        ) : baseline ? (
                            <EmptyState message="No history yet for this merchant" />
                        ) : null}
                    </Stack>
                );

            case 2:
                return (
                    <Stack spacing={1}>
                        <SectionHeader
                            title="Amount Anomaly Detector"
                            subtitle="Z-score analysis against baseline"
                            actions={
                                <FormControlLabel
                                    control={<Switch size="small" checked={useAi} onChange={e => setUseAi(e.target.checked)} />}
                                    label={<Typography variant="caption">AI Assessment</Typography>}
                                />
                            }
                        />
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Transaction ID" value={amtTxnId}
                                onChange={e => setAmtTxnId(e.target.value)} sx={{ width: 200 }} />
                            <Button size="small" variant="contained" disabled={!amtTxnId || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => anomalyAmount(parseInt(amtTxnId), useAi));
                                    if (r?.data?.success) setAmtResult(r.data.data);
                                }}>Check</Button>
                        </Stack>
                        {amtResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <SeverityBadge level={amtResult.anomaly ? amtResult.severity : 'info'} />
                                    <Chip size="small" variant="outlined" label={`z-score ${amtResult.z_score}`} />
                                    <Typography variant="caption">
                                        {fmtMoney(amtResult.current_amount)} vs avg {fmtMoney(amtResult.baseline_mean)}
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {amtResult.reasoning || amtResult.reason}
                                </Typography>
                                <AIAnalysisBox text={amtResult.ai_assessment} />
                            </Paper>
                        )}
                    </Stack>
                );

            case 3:
                return velocity ? (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Velocity Spike Detector"
                            subtitle="Transaction frequency & volume vs. rolling baseline"
                            actions={
                                <Stack direction="row" spacing={1}>
                                    <TextField size="small" label="Window (hrs)" type="number" value={velWindow}
                                        onChange={e => setVelWindow(parseInt(e.target.value) || 24)} sx={{ width: 120 }} />
                                    <Button size="small" variant="outlined" onClick={() => { setVelocity(null); run(() => anomalyVelocity(velWindow)).then(r => r?.data?.success && setVelocity(r.data.data)); }}>Refresh</Button>
                                </Stack>
                            }
                        />
                        <SummaryGrid cards={[
                            { label: `Current Count (${velWindow}h)`, value: velocity.current_count, accent: 'primary' },
                            { label: `Current Amount (${velWindow}h)`, value: fmtMoney(velocity.current_amount), accent: 'info' },
                            { label: 'Prior Avg Count', value: velocity.prior_window_avg_count, accent: 'neutral' },
                            { label: 'Status', value: velocity.count_spike || velocity.amount_spike ? velocity.severity : 'NORMAL', accent: velocity.count_spike || velocity.amount_spike ? 'danger' : 'success' },
                        ]} />
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="caption" fontWeight={600}>Window Comparison</Typography>
                            <SimpleBarChart
                                data={[
                                    { name: 'Current', count: velocity.current_count, amount: velocity.current_amount },
                                    { name: 'Prior Avg', count: velocity.prior_window_avg_count, amount: velocity.prior_window_avg_amount || 0 },
                                ]}
                                xKey="name"
                                series={[
                                    { key: 'count', label: 'Count', color: CHART_COLORS.primary },
                                    { key: 'amount', label: 'Amount', color: CHART_COLORS.secondary },
                                ]}
                                height={220}
                            />
                        </Paper>
                    </Stack>
                ) : <LoadingBlock rows={3} />;

            case 4:
                return newCp ? (
                    <Stack spacing={1}>
                        <SectionHeader title="New Counterparty Alert" subtitle="First-time merchants/vendors in the last 7 days" />
                        <Typography variant="caption">{newCp.new_counterparty_count} new counterparties</Typography>
                        <DrillTable
                            columns={[
                                { key: 'merchant', label: 'Merchant' },
                                { key: 'date', label: 'Date', render: r => fmtDate(r.date) },
                                { key: 'amount', label: 'Amount', align: 'right', render: r => fmtMoney(r.amount) },
                                { key: 'risk_score', label: 'Risk', align: 'right', render: r => <Chip size="small" color={r.risk_score >= 3 ? 'error' : r.risk_score >= 2 ? 'warning' : 'default'} label={r.risk_score} /> },
                                { key: 'action', label: '', render: () => <Button size="small" variant="outlined">Whitelist</Button> },
                            ]}
                            rows={newCp.items || []}
                            rowKeyField="transaction_id"
                            maxHeight={420}
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;

            case 5:
                return dupes ? (
                    <Stack spacing={1}>
                        <SectionHeader title="Duplicate Payment Detector" subtitle="Same vendor + amount within 7 days" />
                        <Typography variant="caption">{dupes.duplicate_candidates} candidates</Typography>
                        <DrillTable
                            columns={[
                                { key: 'vendor_id', label: 'Vendor' },
                                { key: 'amount', label: 'Amount', align: 'right', render: r => fmtMoney(r.amount) },
                                { key: 'bill_ids', label: 'Bill IDs', render: r => r.bill_ids.join(', ') },
                                { key: 'gap_days', label: 'Gap', align: 'right', render: r => `${r.gap_days}d` },
                                { key: 'type', label: 'Type', render: r => (
                                    <Chip size="small" color={r.likely_legitimate ? 'default' : 'warning'}
                                        label={r.likely_legitimate ? 'Recurring' : 'Review'} />
                                )},
                                { key: 'action', label: '', render: r => !r.likely_legitimate && <Button size="small" color="error" variant="outlined">Void</Button> },
                            ]}
                            rows={dupes.items || []}
                            rowKeyField="bill_ids"
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;

            case 6:
                return weekend ? (
                    <Stack spacing={1}>
                        <SectionHeader title="Weekend / Off-Hours Flagger" subtitle="Transactions posted outside business hours" />
                        <Typography variant="caption">{weekend.flagged_count} flagged</Typography>
                        <DrillTable
                            columns={[
                                { key: 'transaction_id', label: 'ID' },
                                { key: 'date', label: 'Date', render: r => fmtDate(r.date) },
                                { key: 'merchant', label: 'Merchant' },
                                { key: 'amount', label: 'Amount', align: 'right', render: r => fmtMoney(r.amount) },
                                { key: 'flags', label: 'Flags', render: r => (
                                    <Stack direction="row" spacing={0.5}>
                                        {r.is_weekend && <Chip size="small" label="weekend" />}
                                        {r.is_off_hours && <Chip size="small" label="off-hours" />}
                                    </Stack>
                                )},
                            ]}
                            rows={weekend.items || []}
                            rowKeyField="transaction_id"
                            maxHeight={420}
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;

            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Anomaly Detection Agent"
            subtitle="Agentic Transaction Intelligence — composite risk scan, Benford's Law, behavioral baselines, velocity spikes, duplicates."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
